import { useState, useCallback } from 'preact/hooks';
import type { I18nStrings } from '@bitio-builder/shared';

export interface UserIntro {
  name: string;
  email: string;
}

interface IntroFormProps {
  strings: I18nStrings;
  onSubmit: (userIntro: UserIntro) => void;
  onBack: () => void;
  isLoading?: boolean;
}

const containerStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 24px',
  textAlign: 'center',
};

const titleStyle: Record<string, string | number> = {
  fontSize: '18px',
  fontWeight: 600,
  color: 'var(--bb-foreground, #1A1C17)',
  marginBottom: '8px',
};

const subtitleStyle: Record<string, string | number> = {
  fontSize: '14px',
  color: 'var(--bb-muted, #6B7280)',
  marginBottom: '24px',
};

const formStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
  maxWidth: '280px',
};

const fieldStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  textAlign: 'left',
};

const labelStyle: Record<string, string | number> = {
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--bb-foreground, #1A1C17)',
};

const inputStyle: Record<string, string | number> = {
  padding: '12px 14px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
};

const inputErrorStyle: Record<string, string | number> = {
  ...inputStyle,
  borderColor: 'var(--bb-error, #DC2626)',
};

const errorMessageStyle: Record<string, string | number> = {
  fontSize: '11px',
  color: 'var(--bb-error, #DC2626)',
};

const submitButtonStyle: Record<string, string | number> = {
  width: '100%',
  padding: '14px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  border: 'none',
  borderRadius: '12px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '8px',
};

const backLinkStyle: Record<string, string | number> = {
  marginTop: '16px',
  fontSize: '13px',
  color: 'var(--bb-muted, #6B7280)',
  cursor: 'pointer',
  textDecoration: 'underline',
  background: 'none',
  border: 'none',
  fontFamily: 'inherit',
};

interface FormErrors {
  name?: string;
  email?: string;
}

export function IntroForm({ strings, onSubmit, onBack, isLoading }: IntroFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    const v = strings.leaveMessage.validation;

    if (!name.trim()) {
      newErrors.name = v.nameRequired;
    }

    if (!email.trim()) {
      newErrors.email = v.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = v.emailInvalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, email, strings]);

  const handleSubmit = useCallback(
    (e: Event) => {
      e.preventDefault();
      if (!validate()) return;
      onSubmit({ name: name.trim(), email: email.trim() });
    },
    [name, email, validate, onSubmit]
  );

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>{strings.introForm.title}</h2>
      <p style={subtitleStyle}>{strings.introForm.subtitle}</p>

      <form style={formStyle} onSubmit={handleSubmit}>
        <div style={fieldStyle}>
          <label style={labelStyle}>{strings.introForm.name} *</label>
          <input
            type="text"
            value={name}
            onInput={(e) => {
              setName((e.target as HTMLInputElement).value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            style={errors.name ? inputErrorStyle : inputStyle}
            disabled={isLoading}
            placeholder={strings.introForm.namePlaceholder}
          />
          {errors.name && <span style={errorMessageStyle}>{errors.name}</span>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>{strings.introForm.email} *</label>
          <input
            type="email"
            value={email}
            onInput={(e) => {
              setEmail((e.target as HTMLInputElement).value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            style={errors.email ? inputErrorStyle : inputStyle}
            disabled={isLoading}
            placeholder={strings.introForm.emailPlaceholder}
          />
          {errors.email && <span style={errorMessageStyle}>{errors.email}</span>}
        </div>

        <button
          type="submit"
          style={{ ...submitButtonStyle, opacity: isLoading ? 0.7 : 1 }}
          disabled={isLoading}
        >
          {isLoading ? strings.introForm.starting : strings.introForm.start}
        </button>
      </form>

      <button style={backLinkStyle} onClick={onBack} disabled={isLoading}>
        {strings.leaveMessage.back}
      </button>
    </div>
  );
}
